import React from 'react'
import { 
  CheckCircle, 
  XCircle, 
  Award, 
  AlertTriangle,
  TrendingUp,
  Target,
  Shield,
  Calendar,
  ExternalLink
} from 'lucide-react'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface CandidateSkill {
  uuid?: string
  skill_id?: number
  skill_name?: string
  skillName?: string
  name?: string
  proficiency_level?: string
  proficiencyLevel?: string
  category?: string
  skillCategory?: string
  isPrimarySkill?: boolean
  yearsOfExperience?: number
  certificationRelated?: boolean
}

interface CandidateCertification {
  uuid?: string
  certification_id?: number
  certification_name?: string
  certificationName?: string
  name?: string
  certification_body?: string
  certificationBody?: string
  issuer?: string
  issue_date?: string
  issueDate?: string
  expiry_date?: string
  expiryDate?: string
  is_verified?: boolean
  isVerified?: boolean
  credential_id?: string
  credentialId?: string
  credential_url?: string
  credentialUrl?: string
}

interface SkillMatchAnalysis {
  score: number
  totalPossible: number
  percentage: number
  requiredMatched: PositionSkill[]
  requiredMissing: PositionSkill[]
  goodToHaveMatched: PositionSkill[]
  goodToHaveMissing: PositionSkill[]
  candidateExtraSkills: CandidateSkill[]
  allCandidateSkills: CandidateSkill[]
}

interface CertificationAnalysis {
  totalRequired: number
  totalRecommended: number
  candidateMatched: PositionCertification[]
  candidateMissing: PositionCertification[]
  candidateExtra: CandidateCertification[]
  matchPercentage: number
}

interface HoverCombinedDetailsProps {
  skillAnalysis: SkillMatchAnalysis
  positionCertifications: PositionCertification[]
  candidateCertifications?: CandidateCertification[]
  className?: string
}

const HoverCombinedDetails: React.FC<HoverCombinedDetailsProps> = ({ 
  skillAnalysis,
  positionCertifications = [],
  candidateCertifications = [],
  className = '' 
}) => {
  
  // Normalize certification names for comparison
  const normalizeCertName = (name: string): string => {
    return name?.toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      || ''
  }

  // Extract certification name from candidate certification object
  const extractCandidateCertName = (cert: CandidateCertification): string => {
    const certName = cert?.certificationName || 
                    cert?.certification_name || 
                    cert?.name || 
                    ''
    return normalizeCertName(certName)
  }

  // Check if certifications match
  const certificationsMatch = (posCert: PositionCertification, candCert: CandidateCertification): boolean => {
    const posCertName = normalizeCertName(posCert.certification_name)
    const candCertName = extractCandidateCertName(candCert)
    
    if (posCertName && candCertName) {
      // Exact match
      if (posCertName === candCertName) {
        return true
      }
      
      // Partial match for compound certifications
      if (posCertName.includes(candCertName) || candCertName.includes(posCertName)) {
        return true
      }
      
      // Handle common variations
      const variations: Record<string, string[]> = {
        'aws': ['amazon web services', 'aws certified'],
        'pmp': ['project management professional'],
        'cissp': ['certified information systems security professional'],
        'cisa': ['certified information systems auditor'],
        'cism': ['certified information security manager'],
        'ciscoccessecurity': ['cisco certified security'],
        'azure': ['microsoft azure', 'az'],
        'gcp': ['google cloud platform', 'google cloud certified'],
        'scrum': ['certified scrummaster', 'csm', 'psm'],
        'agile': ['agile certified practitioner', 'acp'],
        'itil': ['information technology infrastructure library'],
        'comptia': ['computing technology industry association']
      }
      
      for (const [key, alts] of Object.entries(variations)) {
        if (posCertName.includes(key) && alts.some(alt => candCertName.includes(alt))) return true
        if (candCertName.includes(key) && alts.some(alt => posCertName.includes(alt))) return true
      }
    }
    
    return false
  }

  // Analyze certifications
  const analyzeCertifications = (): CertificationAnalysis => {
    if (!positionCertifications || positionCertifications.length === 0) {
      return { 
        totalRequired: 0,
        totalRecommended: 0,
        candidateMatched: [],
        candidateMissing: [],
        candidateExtra: candidateCertifications || [],
        matchPercentage: 0
      }
    }

    const requiredCerts = positionCertifications.filter(cert => cert.is_required)
    const recommendedCerts = positionCertifications.filter(cert => !cert.is_required)
    
    const candidateMatched: PositionCertification[] = []
    const candidateMissing: PositionCertification[] = []

    // Check required and recommended certifications
    positionCertifications.forEach(posCert => {
      const hasMatch = candidateCertifications.some(candCert => 
        certificationsMatch(posCert, candCert)
      )
      
      if (hasMatch) {
        candidateMatched.push(posCert)
      } else {
        candidateMissing.push(posCert)
      }
    })

    // Find extra certifications
    const allPositionCertNames = positionCertifications.map(c => normalizeCertName(c.certification_name))
    const candidateExtra = candidateCertifications.filter(candCert => {
      const certName = extractCandidateCertName(candCert)
      return certName && !allPositionCertNames.some(posCertName => 
        posCertName === certName || 
        posCertName.includes(certName) || 
        certName.includes(posCertName)
      )
    })

    const totalCerts = positionCertifications.length
    const matchedCerts = candidateMatched.length
    const matchPercentage = totalCerts > 0 ? Math.round((matchedCerts / totalCerts) * 100) : 0

    return {
      totalRequired: requiredCerts.length,
      totalRecommended: recommendedCerts.length,
      candidateMatched,
      candidateMissing,
      candidateExtra,
      matchPercentage
    }
  }

  const certAnalysis = analyzeCertifications()

  const getSkillScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600'
    if (percentage >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getSkillScoreIcon = (percentage: number) => {
    if (percentage >= 80) return <Award className="w-4 h-4" />
    if (percentage >= 60) return <CheckCircle className="w-4 h-4" />
    return <AlertTriangle className="w-4 h-4" />
  }

  const getCertMatchColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600'
    if (percentage >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getCertMatchIcon = (percentage: number) => {
    if (percentage >= 80) return <Shield className="w-4 h-4" />
    if (percentage >= 60) return <CheckCircle className="w-4 h-4" />
    return <AlertTriangle className="w-4 h-4" />
  }

  const extractSkillName = (skill: CandidateSkill): string => {
    return skill?.skillName || skill?.skill_name || skill?.name || ''
  }

  const isExpired = (expiryDate?: string): boolean => {
    if (!expiryDate) return false
    return new Date(expiryDate) < new Date()
  }

  const isExpiringSoon = (expiryDate?: string): boolean => {
    if (!expiryDate) return false
    const expiry = new Date(expiryDate)
    const sixMonthsFromNow = new Date()
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6)
    return expiry < sixMonthsFromNow && expiry > new Date()
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-md ${className}`}>
      {/* Combined Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center space-x-4">
          {/* Skills Score */}
          <div className="flex items-center space-x-1">
            <div className={getSkillScoreColor(skillAnalysis.percentage)}>
              {getSkillScoreIcon(skillAnalysis.percentage)}
            </div>
            <span className={`font-semibold text-sm ${getSkillScoreColor(skillAnalysis.percentage)}`}>
              {skillAnalysis.percentage}% Skills
            </span>
          </div>
          
          {/* Certifications Score */}
          {positionCertifications.length > 0 && (
            <>
              <div className="w-px h-4 bg-gray-300"></div>
              <div className="flex items-center space-x-1">
                <div className={getCertMatchColor(certAnalysis.matchPercentage)}>
                  {getCertMatchIcon(certAnalysis.matchPercentage)}
                </div>
                <span className={`font-semibold text-sm ${getCertMatchColor(certAnalysis.matchPercentage)}`}>
                  {certAnalysis.matchPercentage}% Certs
                </span>
              </div>
            </>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {skillAnalysis.score}/{skillAnalysis.totalPossible} pts
        </div>
      </div>

      <div className="space-y-4 text-xs">
        {/* SKILLS SECTION */}
        <div>
          <div className="flex items-center mb-2">
            <Award className="w-3 h-3 text-blue-600 mr-1" />
            <span className="font-medium text-blue-700 text-sm">Skills Match</span>
          </div>
          
          <div className="space-y-2 ml-4">
            {/* Required Skills Matched */}
            {skillAnalysis.requiredMatched.length > 0 && (
              <div>
                <div className="flex items-center mb-1">
                  <CheckCircle className="w-3 h-3 text-green-600 mr-1" />
                  <span className="font-medium text-green-700">
                    Required ({skillAnalysis.requiredMatched.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 ml-4">
                  {skillAnalysis.requiredMatched.map((skill, index) => (
                    <span 
                      key={index}
                      className="inline-block px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs"
                    >
                      {skill.skill_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Required Skills */}
            {skillAnalysis.requiredMissing.length > 0 && (
              <div>
                <div className="flex items-center mb-1">
                  <XCircle className="w-3 h-3 text-red-600 mr-1" />
                  <span className="font-medium text-red-700">
                    Missing Required ({skillAnalysis.requiredMissing.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 ml-4">
                  {skillAnalysis.requiredMissing.map((skill, index) => (
                    <span 
                      key={index}
                      className="inline-block px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs"
                    >
                      {skill.skill_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Good to Have Skills Matched */}
            {skillAnalysis.goodToHaveMatched.length > 0 && (
              <div>
                <div className="flex items-center mb-1">
                  <Target className="w-3 h-3 text-blue-600 mr-1" />
                  <span className="font-medium text-blue-700">
                    Good-to-Have ({skillAnalysis.goodToHaveMatched.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 ml-4">
                  {skillAnalysis.goodToHaveMatched.map((skill, index) => (
                    <span 
                      key={index}
                      className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                    >
                      {skill.skill_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* All Candidate Skills */}
            {skillAnalysis.allCandidateSkills && skillAnalysis.allCandidateSkills.length > 0 && (
              <div>
                <div className="flex items-center mb-1">
                  <TrendingUp className="w-3 h-3 text-purple-600 mr-1" />
                  <span className="font-medium text-purple-700">
                    All Candidate Skills ({skillAnalysis.allCandidateSkills.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 ml-4">
                  {skillAnalysis.allCandidateSkills.slice(0, 6).map((skill, index) => (
                    <span 
                      key={index}
                      className="inline-block px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs"
                    >
                      {extractSkillName(skill)}
                    </span>
                  ))}
                  {skillAnalysis.allCandidateSkills.length > 6 && (
                    <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      +{skillAnalysis.allCandidateSkills.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CERTIFICATIONS SECTION */}
        {positionCertifications.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center mb-2">
              <Shield className="w-3 h-3 text-orange-600 mr-1" />
              <span className="font-medium text-orange-700 text-sm">Certifications</span>
            </div>
            
            <div className="space-y-2 ml-4">
              {/* Matched Certifications */}
              {certAnalysis.candidateMatched.length > 0 && (
                <div>
                  <div className="flex items-center mb-1">
                    <CheckCircle className="w-3 h-3 text-green-600 mr-1" />
                    <span className="font-medium text-green-700">
                      Has Certifications ({certAnalysis.candidateMatched.length})
                    </span>
                  </div>
                  <div className="space-y-1 ml-4">
                    {certAnalysis.candidateMatched.map((cert, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-green-800 bg-green-100 px-2 py-0.5 rounded text-xs">
                          {cert.certification_name}
                        </span>
                        {cert.is_required && (
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Certifications */}
              {certAnalysis.candidateMissing.length > 0 && (
                <div>
                  <div className="flex items-center mb-1">
                    <XCircle className="w-3 h-3 text-red-600 mr-1" />
                    <span className="font-medium text-red-700">
                      Missing ({certAnalysis.candidateMissing.length})
                    </span>
                  </div>
                  <div className="space-y-1 ml-4">
                    {certAnalysis.candidateMissing.map((cert, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-red-800 bg-red-100 px-2 py-0.5 rounded text-xs">
                          {cert.certification_name}
                        </span>
                        {cert.is_required && (
                          <span className="text-xs text-red-600 font-bold">Required</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extra Certifications */}
              {certAnalysis.candidateExtra.length > 0 && (
                <div>
                  <div className="flex items-center mb-1">
                    <Shield className="w-3 h-3 text-purple-600 mr-1" />
                    <span className="font-medium text-purple-700">
                      Additional ({certAnalysis.candidateExtra.length})
                    </span>
                  </div>
                  <div className="space-y-1 ml-4">
                    {certAnalysis.candidateExtra.slice(0, 2).map((cert, index) => {
                      const certName = extractCandidateCertName(cert)
                      const expiry = cert?.expiryDate || cert?.expiry_date
                      return (
                        <div key={index} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-purple-800 bg-purple-100 px-2 py-0.5 rounded text-xs">
                              {certName}
                            </span>
                            {cert?.credential_url && (
                              <ExternalLink className="w-3 h-3 text-blue-500" />
                            )}
                          </div>
                          {expiry && (
                            <div className="flex items-center text-xs ml-2">
                              <Calendar className="w-2 h-2 mr-1" />
                              <span className={`${
                                isExpired(expiry) ? 'text-red-600' : 
                                isExpiringSoon(expiry) ? 'text-yellow-600' : 'text-gray-600'
                              }`}>
                                Expires: {new Date(expiry).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {certAnalysis.candidateExtra.length > 2 && (
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        +{certAnalysis.candidateExtra.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Summary Footer */}
        <div className="border-t border-gray-100 pt-2 text-xs text-gray-600">
          <div className="flex justify-between items-center">
            <span>Skills: {skillAnalysis.score}/{skillAnalysis.totalPossible} pts</span>
            {positionCertifications.length > 0 && (
              <span>Certs: {certAnalysis.candidateMatched.length}/{positionCertifications.length}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HoverCombinedDetails